/**
 * Working out what a column is.
 *
 * The rule the whole file is built around: **a column has one type, decided by
 * a vote across its values, and never per value.** Guessing cell by cell is how
 * `03/04/2026` becomes March in one row and April in the next, how a reference
 * number starting `0044` loses its zeros in some rows and not others, and how a
 * column of mostly-numbers with one "n/a" in it ends up as text. A vote is also
 * what makes a confidence number meaningful, and the confidence is what decides
 * whether the review screen states the type or asks about it.
 *
 * Nothing here needs a model. Types are the one thing a model must not decide:
 * being wrong about a label costs a click, and being wrong about a type produces
 * a document that is silently incorrect.
 */
import type { FieldType } from "@broadpaper/core";
import { tidyLabel } from "./names.js";

export interface ColumnVote {
  type: FieldType;
  /** A display pattern in the form `FieldSchema.format` takes. */
  format?: string;
  currency?: string;
  /** Share of non-empty values the winning type explains, 0–1. */
  confidence: number;
  /**
   * Both day-first and month-first explain every value in the column.
   *
   * Not a failure — it is genuinely undecidable from the data, which is exactly
   * the kind of thing to ask the user about rather than quietly pick. The
   * caller's `dayFirst` decides the default and this flags the question.
   */
  ambiguousDateOrder?: boolean;
  /** Every non-empty value is identical. */
  constant: boolean;
  blankRatio: number;
  samples: unknown[];
}

/**
 * Date patterns tried against a column, in the order they are preferred.
 *
 * Each is a regex plus the field order it implies. ISO first because it is
 * unambiguous and because it is what an export from any system produces; the
 * slashed forms after it, because they are what a person typed.
 */
interface DatePattern {
  /** What `FieldSchema.format` will say, i.e. how the value is displayed. */
  display: string;
  re: RegExp;
  order: "ymd" | "dmy" | "mdy" | "text";
  hasTime: boolean;
}

const DATE_PATTERNS: DatePattern[] = [
  { display: "d MMMM yyyy", re: /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:Z|[+-]\d{2}:?\d{2})?$/, order: "ymd", hasTime: false },
  { display: "d MMMM yyyy", re: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, order: "ymd", hasTime: false },
  { display: "d MMMM yyyy", re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: "dmy", hasTime: false },
  { display: "d MMMM yyyy", re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, order: "dmy", hasTime: false },
  { display: "d MMMM yyyy", re: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, order: "dmy", hasTime: false },
  // `31 Aug 2026`, `Aug 31 2026`, `31 August 2026` — the month is named, so the
  // order cannot be ambiguous however the numbers fall.
  { display: "d MMMM yyyy", re: /^(\d{1,2})\s+([A-Za-z]{3,})\.?,?\s+(\d{4})$/, order: "text", hasTime: false },
  { display: "d MMMM yyyy", re: /^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/, order: "text", hasTime: false }
];

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Currency symbols common enough to be worth recognising, and their ISO codes.
 *
 * Longest first, because they are found with `includes`: listed after `$`, the real
 * `R$` was never reached and a column of Brazilian reais came in as dollars.
 */
const SYMBOLS: Array<[string, string]> = [
  ["R$", "BRL"],
  ["CHF", "CHF"],
  ["zł", "PLN"],
  ["kr", "SEK"],
  ["£", "GBP"],
  ["$", "USD"],
  ["€", "EUR"],
  ["¥", "JPY"],
  ["₹", "INR"]
];

/**
 * The symbols above that are written with letters, which `parseNumeric` would
 * otherwise refuse — any other letter means a value is not a number.
 *
 * `kr` only with a space between it and the figure, and only in lower case, the way
 * it is written: a bare `KR100` is a code, and reading it as 100 is the `INV-10241`
 * mistake again. `CHF` needs nothing here; it is an ISO code and goes with the others.
 */
const LETTERED_SYMBOL_BEFORE = /^(R\$\s*|zł\s*|kr\.?\s+)/;
const LETTERED_SYMBOL_AFTER = /(\s*R\$|\s*zł|\s+kr\.?)$/;

const TRUE_WORDS = new Set(["true", "yes", "y", "t"]);
const FALSE_WORDS = new Set(["false", "no", "n", "f"]);

/** Yes, no, or neither, from the words a spreadsheet or a person uses for them. */
export function booleanWord(text: string): boolean | null {
  const s = text.trim().toLowerCase();
  return TRUE_WORDS.has(s) ? true : FALSE_WORDS.has(s) ? false : null;
}

const FULL_MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

/** Header words that reinforce a reading the values alone leave open. */
const MONEY_WORDS = /\b(amount|total|price|cost|fee|fees|value|balance|salary|revenue|paid|due|subtotal|net|gross|vat|tax|charge|premium|invoiced|spend|budget)\b/i;
const RATE_WORDS = /(\brate\b|\byield\b|\bmargin\b|\bgrowth\b|\bchange\b|\bshare\b|\ballocation\b|\bweight\b|%|percent)/i;
const ID_WORDS = /\b(id|ref|reference|number|no|code|sku|account|invoice|postcode|zip|phone|tel|mobile|vat|ni|nino|isbn)\b/i;
const VAT_REGISTRATION = /\bvat\s+(reg|registration|number|no|id)\b/i;

/**
 * Votes on a column's type.
 *
 * `header` is used only to break ties the values leave open — whether a column
 * of plain numbers is money, whether a column of small decimals is a
 * percentage, and whether a column of digits is really a reference that must
 * stay text. It never overrides what the values say.
 */
export function inferColumn(rawHeader: string, values: unknown[], opts: { dayFirst?: boolean } = {}): ColumnVote {
  const dayFirst = opts.dayFirst !== false;
  // The header's words, separated. `invoiceNumber` and `client.name` have no
  // word boundary where a person reads one, so every `\b`-anchored test below
  // would miss — which is how an invoice number came out as a number.
  const header = rawHeader
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.\-/]+/g, " ")
    .trim();
  const total = values.length;
  const present = values.filter((v) => !blank(v));
  const blankRatio = total === 0 ? 1 : 1 - present.length / total;
  const samples = distinct(present, 4);
  const constant = present.length > 1 && samples.length === 1;
  const base = { constant, blankRatio, samples };

  // An empty column is text. There is nothing to vote on, and text is the only
  // type that cannot make a document wrong.
  if (!present.length) return { type: "string", confidence: 0, ...base };

  // A value that arrived already typed — a date out of a workbook's style
  // table, a boolean, a number — is not re-derived from its string form.
  const typed = present.filter((v) => v instanceof Date).length;
  if (typed === present.length) {
    const withTime = present.some((v) => (v as Date).getUTCHours() !== 0 || (v as Date).getUTCMinutes() !== 0);
    return { type: withTime ? "datetime" : "date", format: withTime ? "d MMMM yyyy, HH:mm" : "d MMMM yyyy", confidence: 1, ...base };
  }
  const bools = present.filter((v) => typeof v === "boolean").length;
  if (bools === present.length) return { type: "boolean", confidence: 1, ...base };

  const strings = present.map((v) => (typeof v === "string" ? v.trim() : String(v)));

  /* ------------------------------- boolean ------------------------------- */
  const boolish = strings.filter((s) => TRUE_WORDS.has(s.toLowerCase()) || FALSE_WORDS.has(s.toLowerCase())).length;
  if (boolish === strings.length) return { type: "boolean", confidence: 1, ...base };

  /* ------------------------------ date, dates ---------------------------- */
  const date = voteDate(strings, dayFirst);
  if (date && date.share >= 0.95) {
    const vote: ColumnVote = { type: date.hasTime ? "datetime" : "date", format: date.hasTime ? "d MMMM yyyy, HH:mm" : date.display, confidence: date.share, ...base };
    if (date.ambiguous) vote.ambiguousDateOrder = true;
    return vote;
  }

  /* ------------------------------- numbers ------------------------------- */
  const numeric = strings.map(parseNumeric);
  const numericShare = numeric.filter((n) => n !== null).length / strings.length;

  // A column of digits that is plainly an identifier stays text, whatever it
  // parses as. Leading zeros are the giveaway that does not need the header at
  // all: `0044` is a code, and a number would lose the zeros in the document.
  // Spaces between the groups too, because that is how a phone number is written:
  // `07700 900123` parses once the space goes, and printed 7700900123.
  const leadingZeros = strings.some((s) => /^0\d[\d ]*$/.test(s));
  // "VAT" is a money word and an identifier word at once. On its own it is the tax;
  // with "number" or "reg" after it, it is the registration, which is digits that
  // must print as they were written.
  const identifying = ID_WORDS.test(header) && (!MONEY_WORDS.test(header) || VAT_REGISTRATION.test(header));
  if (leadingZeros || (numericShare === 1 && identifying && strings.every((s) => /^\d+( \d+)*$/.test(s)))) {
    return { type: "string", confidence: 1, ...base };
  }

  if (numericShare >= 0.95) {
    const currency = voteCurrency(strings, header);
    if (currency) return { type: "currency", currency, format: undefined, confidence: numericShare, ...base };

    const percent = votePercent(strings, header, numeric);
    if (percent) return { type: "percent", format: percent.format, confidence: numericShare, ...base };

    return { type: "number", confidence: numericShare, ...base };
  }

  /* -------------------------------- strings ------------------------------ */
  if (strings.every((s) => /^(https?:)?\/\/|^www\./i.test(s))) return { type: "url", confidence: 1, ...base };

  return { type: "string", confidence: 1, ...base };
}

/* ------------------------------- coercion -------------------------------- */

/**
 * The value a row should actually carry, given the column's decided type.
 *
 * Dates become **ISO strings**, and that is the important one. `new Date("03/04/2026")`
 * reads month first whatever the file meant, so leaving the original text in
 * place would mean every renderer in the system re-deciding the ambiguity this
 * module already resolved — differently, and invisibly. Normalising here makes
 * the decision once.
 */
export function coerce(value: unknown, vote: ColumnVote, opts: { dayFirst?: boolean } = {}): unknown {
  if (blank(value)) return null;
  switch (vote.type) {
    case "date":
    case "datetime": {
      if (value instanceof Date) return iso(value, vote.type === "datetime");
      const parsed = parseDate(String(value).trim(), opts.dayFirst !== false);
      return parsed ? iso(parsed.date, vote.type === "datetime" || parsed.hasTime) : null;
    }
    case "boolean": {
      if (typeof value === "boolean") return value;
      const s = String(value).trim().toLowerCase();
      if (TRUE_WORDS.has(s)) return true;
      if (FALSE_WORDS.has(s)) return false;
      return null;
    }
    case "number":
    case "currency":
      return typeof value === "number" ? value : parseNumeric(String(value));
    case "percent": {
      const n = typeof value === "number" ? value : parseNumeric(String(value));
      if (n === null) return null;
      // The percent pipe expects a fraction, so a column written `12.5%` or
      // `12.5` with a percent heading is divided here — once, at the edge,
      // rather than by whoever eventually binds it.
      // Rounded, because `1.1 / 100` is 0.011000000000000001 — which prints the same
      // and compares unequal with the 0.011 a person typed into the grid.
      return vote.format === "scaled" ? Math.round((n / 100) * 1e9) / 1e9 : n;
    }
    default:
      return typeof value === "string" ? value.trim() : value instanceof Date ? iso(value, false) : value === null || value === undefined ? null : String(value);
  }
}

/* -------------------------------- helpers -------------------------------- */

export function blank(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === "string" && v.trim() === "");
}

function distinct(values: unknown[], n: number): unknown[] {
  const out: unknown[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    const key = v instanceof Date ? v.toISOString() : String(v);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
    if (out.length >= n) break;
  }
  return out;
}

function iso(date: Date, withTime: boolean): string {
  const s = date.toISOString();
  return withTime ? s : s.slice(0, 10);
}

/**
 * A number out of whatever a spreadsheet wrote.
 *
 * Handles thousands separators, a currency symbol either side, a trailing
 * percent, and accounting negatives in brackets. Returns null rather than NaN
 * so a caller cannot accidentally arithmetic with a failure.
 *
 * Deliberately refuses a comma-as-decimal-point reading, and refuses it by
 * reading nothing rather than by guessing. `1,234` is one thousand two hundred and
 * thirty-four in every file this product has seen, so a comma is a thousands
 * separator — but only where it sits between groups of three. Stripping every
 * comma regardless is how `12,5` came in as 125 and `1.234,56` as a failure that
 * looked like a value: a figure ten times too big, in a column nobody questioned.
 */
export function parseNumeric(text: string): number | null {
  let s = text.trim();
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1).trim();
  }

  // Strip what decorates a number and nothing else. Stripping *letters* is how
  // `INV-10241` became −10241 and a column of invoice numbers came out as a
  // column of negative integers — on a document, under a heading that said
  // "Invoice number". So a currency symbol goes, a known ISO code goes, a
  // trailing percent goes, and any other letter means this is not a number.
  // The symbols spelt with letters go first, before `zł` loses its `ł` to the
  // generic strip and leaves a `z` that refuses the whole value.
  s = s.replace(LETTERED_SYMBOL_BEFORE, "").replace(LETTERED_SYMBOL_AFTER, "");
  s = s.replace(/^[^\dA-Za-z.+-]+/, "").replace(/[^\d%A-Za-z.]+$/, "");
  s = s.replace(/^(GBP|USD|EUR|JPY|AUD|CAD|CHF|SEK|NOK|DKK|NZD|INR|ZAR|PLN|SGD|HKD|BRL)\s*/i, "");
  s = s.replace(/\s*(GBP|USD|EUR|JPY|AUD|CAD|CHF|SEK|NOK|DKK|NZD|INR|ZAR|PLN|SGD|HKD|BRL)$/i, "");
  s = s.replace(/%$/, "");
  s = s.replace(/\s/g, "");
  if (s.includes(",")) {
    if (!/^[-+]?\d{1,3}(,\d{3})+(\.\d*)?$/.test(s)) return null;
    s = s.replace(/,/g, "");
  }

  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/**
 * An ISO code written against the figure, either side — `USD 0.76`, `12.50 EUR`.
 * The same codes `parseNumeric` strips, because a code it strips as decoration has to
 * count as evidence here, or the column keeps the figures and loses what they were in.
 */
const CODE_IN_VALUE = /^(GBP|USD|EUR|JPY|AUD|CAD|CHF|SEK|NOK|DKK|NZD|INR|ZAR|PLN|SGD|HKD)\s*[-+(]?[\d.]|[\d.)]\s*(GBP|USD|EUR|JPY|AUD|CAD|CHF|SEK|NOK|DKK|NZD|INR|ZAR|PLN|SGD|HKD)$/i;

function voteCurrency(strings: string[], header: string): string | undefined {
  const counts = new Map<string, number>();
  for (const s of strings) {
    const symbol = SYMBOLS.find(([sym]) => s.includes(sym));
    const written = symbol ? undefined : CODE_IN_VALUE.exec(s);
    const code = symbol?.[1] ?? (written ? (written[1] ?? written[2])!.toUpperCase() : undefined);
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  // A symbol or a code in the values is decisive. Mixed symbols are not money in one
  // currency, so they stay a plain number rather than being averaged into one.
  if (counts.size === 1) {
    const [code, n] = [...counts][0]!;
    if (n / strings.length >= 0.5) return code;
  }
  if (counts.size > 1) return undefined;

  // No symbol anywhere: the heading decides, and only if it is unmistakably
  // about money. An ISO code in the heading — `Fees (GBP)` — is better evidence
  // than the words are.
  const code = /\b(GBP|USD|EUR|JPY|AUD|CAD|CHF|SEK|NOK|DKK|NZD|INR|ZAR|PLN|SGD|HKD)\b/i.exec(header)?.[1];
  if (code) return code.toUpperCase();
  const symbol = SYMBOLS.find(([sym]) => header.includes(sym));
  if (symbol) return symbol[1];
  if (MONEY_WORDS.test(header) && !RATE_WORDS.test(header)) return undefined;
  return undefined;
}

/**
 * Whether a numeric column is a percentage, and whether it is already scaled.
 *
 * `format: "scaled"` is a marker for {@link coerce}, not a display pattern: it
 * records that the file wrote 12.5 for twelve and a half per cent, so the value
 * has to be divided before it reaches a pipe that expects a fraction. A column
 * written `0.125` needs no division.
 */
function votePercent(strings: string[], header: string, numeric: Array<number | null>): { format?: string } | null {
  const signed = strings.filter((s) => s.includes("%")).length;
  if (signed / strings.length >= 0.5) return { format: "scaled" };
  if (!RATE_WORDS.test(header)) return null;
  // `VAT 20%` names the rate a column of money was worked out at: the column is the
  // tax, not the rate. Read as a rate — as it was, on a real invoice — a tax of 0.15
  // was stored as 0.0015 and printed as a percentage, a wrong figure under a heading
  // that looked right.
  if (/\d\s*%/.test(header) && MONEY_WORDS.test(header)) return null;
  const values = numeric.filter((n): n is number => n !== null);
  if (!values.length) return null;
  // A rate-named column whose values are all within ±1 is already a fraction;
  // one that reaches 12 or 85 was written as whole percentage points. The
  // boundary is deliberately conservative: a "growth rate" of 1.4 is ambiguous,
  // and treating it as 140% is the reading a spreadsheet user expects.
  const max = Math.max(...values.map(Math.abs));
  return max <= 1 ? {} : { format: "scaled" };
}

interface ParsedDate {
  date: Date;
  hasTime: boolean;
}

/** Parses one value against every pattern, in order, with a fixed day/month preference. */
export function parseDate(text: string, dayFirst: boolean): ParsedDate | null {
  for (const p of DATE_PATTERNS) {
    const m = p.re.exec(text);
    if (!m) continue;
    const built = build(p, m, dayFirst);
    if (built) return built;
  }
  return null;
}

function build(p: DatePattern, m: RegExpExecArray, dayFirst: boolean): ParsedDate | null {
  let year: number, month: number, day: number;
  let hour = 0,
    minute = 0,
    second = 0;
  if (p.order === "ymd") {
    year = Number(m[1]);
    month = Number(m[2]);
    day = Number(m[3]);
    if (m[4]) {
      hour = Number(m[4]);
      minute = Number(m[5]);
      second = Number(m[6] ?? "0");
    }
  } else if (p.order === "text") {
    const named = MONTHS.indexOf((m[1] ?? "").slice(0, 3).toLowerCase());
    if (named >= 0) {
      month = named + 1;
      day = Number(m[2]);
      year = Number(m[3]);
    } else {
      const second = MONTHS.indexOf((m[2] ?? "").slice(0, 3).toLowerCase());
      if (second < 0) return null;
      day = Number(m[1]);
      month = second + 1;
      year = Number(m[3]);
    }
  } else {
    const a = Number(m[1]);
    const b = Number(m[2]);
    year = Number(m[3]);
    // Either number above twelve settles the order regardless of preference.
    if (a > 12) {
      day = a;
      month = b;
    } else if (b > 12) {
      day = b;
      month = a;
    } else if (dayFirst) {
      day = a;
      month = b;
    } else {
      day = b;
      month = a;
    }
  }
  if (!valid(year, month, day)) return null;
  // UTC throughout: a date in a spreadsheet is a calendar date, and building it
  // in local time makes it the day before in any negative offset.
  return { date: new Date(Date.UTC(year, month - 1, day, hour, minute, second)), hasTime: hour !== 0 || minute !== 0 || second !== 0 };
}

function valid(year: number, month: number, day: number): boolean {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false;
  if (year < 1000 || year > 3000 || month < 1 || month > 12 || day < 1 || day > 31) return false;
  // The day has to exist in that month, or 31 February is accepted and silently
  // becomes 3 March.
  return new Date(Date.UTC(year, month - 1, day)).getUTCDate() === day;
}

/**
 * Finds the one date pattern that explains the column, and says whether the
 * day/month order was decidable.
 *
 * A column is a date only if a *single* pattern explains at least 95% of it.
 * Accepting "some of these parse as dates somehow" is how a column of mixed
 * references becomes a date column with most of its values missing.
 */
function voteDate(strings: string[], dayFirst: boolean): { display: string; share: number; hasTime: boolean; ambiguous: boolean } | null {
  let best: { p: DatePattern; share: number; hasTime: boolean } | null = null;
  for (const p of DATE_PATTERNS) {
    let hit = 0;
    let hasTime = false;
    for (const s of strings) {
      const m = p.re.exec(s);
      if (!m) continue;
      const built = build(p, m, dayFirst);
      if (!built) continue;
      hit++;
      if (built.hasTime) hasTime = true;
    }
    const share = hit / strings.length;
    if (!best || share > best.share) best = { p, share, hasTime };
  }
  if (!best || best.share < 0.95) return null;

  // Ambiguous when the pattern is a slashed or dashed numeric one and no value
  // in the column has a day above twelve to settle it. Naming the month, or an
  // ISO date, cannot be ambiguous.
  const ambiguous =
    (best.p.order === "dmy" || best.p.order === "mdy") &&
    !strings.some((s) => {
      const m = best!.p.re.exec(s);
      return m ? Number(m[1]) > 12 || Number(m[2]) > 12 : false;
    });

  return { display: best.p.display, share: best.share, hasTime: best.hasTime, ambiguous };
}

/**
 * The pattern a column of dates was written in, spelt the way `FieldSchema.format` takes it.
 *
 * A spreadsheet's dates are printed in one house style, `12 September 2026`, because a
 * CSV's ISO dates are nobody's idea of how a document should read. A table lifted out
 * of a printed document is the opposite case: its dates are already in the style the
 * author chose, and `01/08/2026` coming back as "1 August 2026" is a design that does
 * not look like the document it was made from. Null when no one pattern explains the
 * column, or it carries times; the caller keeps the house style then.
 */
export function writtenDateFormat(strings: string[], dayFirst: boolean): string | null {
  const values = strings.map((s) => s.trim()).filter(Boolean);
  if (!values.length) return null;
  let best: { p: DatePattern; hits: RegExpExecArray[] } | null = null;
  for (const p of DATE_PATTERNS) {
    const hits = values.map((s) => p.re.exec(s)).filter((m): m is RegExpExecArray => m !== null);
    if (!best || hits.length > best.hits.length) best = { p, hits };
  }
  if (!best || best.hits.length / values.length < 0.95) return null;
  const { p, hits } = best;
  const first = hits[0]!;

  // Padded unless a value shows otherwise: `1/8/2026` settles it, `12/11/2026` cannot.
  const padded = (group: number) => !hits.some((m) => m[group]!.length === 1);
  const day = (group: number) => (padded(group) ? "dd" : "d");
  const month = (group: number) => (padded(group) ? "MM" : "M");

  if (p.order === "ymd") {
    if (hits.some((m) => m[4])) return null;
    const sep = first[0].charAt(4);
    return `yyyy${sep}${month(2)}${sep}${day(3)}`;
  }
  if (p.order === "dmy" || p.order === "mdy") {
    const sep = /[/.-]/.exec(first[0])![0];
    // The same rule `build` reads them by: a number above twelve settles which is the
    // day, and only a column with none falls back on the preference.
    const dayLeads = hits.some((m) => Number(m[1]) > 12) ? true : hits.some((m) => Number(m[2]) > 12) ? false : dayFirst;
    return dayLeads ? `${day(1)}${sep}${month(2)}${sep}yyyy` : `${month(1)}${sep}${day(2)}${sep}yyyy`;
  }

  // A named month, before or after the day, spelt out or shortened. Spelt out only when
  // every value is a whole month name, so a column of `Aug` and `May` stays short.
  const monthLeads = /^[A-Za-z]/.test(first[0]);
  const nameGroup = monthLeads ? 1 : 2;
  const named = hits.every((m) => FULL_MONTHS.includes(m[nameGroup]!.toLowerCase())) ? "MMMM" : "MMM";
  const comma = first[0].includes(",") ? "," : "";
  return monthLeads ? `${named} ${day(2)}${comma} yyyy` : `${day(1)} ${named}${comma} yyyy`;
}

/**
 * Whether the first row of a table is headings rather than data.
 *
 * Three signals, all of which have to hold: every cell is text, no two cells
 * say the same thing, and the rows beneath it look different from it. The third
 * is the one that matters — a table of product names has a first row of unique
 * text too, and only comparing it with what follows tells them apart.
 *
 * When there is only one row there is nothing to compare with, and a single row
 * of short text is very much more likely to be headings than to be the only
 * record in the file.
 */
export function looksLikeHeaderRow(rows: unknown[][]): boolean {
  const first = rows[0];
  if (!first || !first.length) return false;
  const cells = first.map((c) => (c === null || c === undefined ? "" : String(c).trim()));
  const filled = cells.filter(Boolean);
  if (filled.length < Math.max(1, Math.ceil(cells.length * 0.6))) return false;
  if (first.some((c) => c instanceof Date || typeof c === "number" || typeof c === "boolean")) return false;
  // Headings are short. A first row of sentences is data.
  if (filled.some((c) => c.length > 80)) return false;
  if (rows.length === 1) return true;

  const body = rows.slice(1, 30);
  let differs = 0;
  let same = 0;
  for (let c = 0; c < cells.length; c++) {
    const column = body.map((r) => r[c]).filter((v) => !blank(v));
    if (!column.length) continue;
    const bodyVote = inferColumn(cells[c] ?? "", column);
    // Only a column that is definitely not text can tell us anything: where the
    // body is text too, a text first row proves nothing either way.
    if (bodyVote.type === "string" || bodyVote.confidence < 0.9) continue;
    // Everything out of a CSV is a string, so "is the first cell a number?"
    // cannot be answered by its JavaScript type — `482310` and `Value` are both
    // strings here. It has to be inferred the same way the column below it was,
    // and then compared: a first cell that reads as the same type as the column
    // is another row of that column, and a first cell that reads as text above
    // a column of numbers is its heading.
    const headerVote = inferColumn("", [cells[c] ?? ""]);
    if (headerVote.type === bodyVote.type) same++;
    else if (headerVote.type === "string") differs++;
  }
  // A row of text above columns that are not text is a heading row, and this
  // signal outranks everything else — including repeated headings, which
  // spreadsheets produce constantly (two columns called Amount, three blank
  // ones) and which would otherwise disqualify a perfectly ordinary file.
  if (differs > same) return true;
  if (same > 0) return false;

  // Everything is text on both sides. Fall back to the shape of the words: a
  // heading row is usually title case or snake case, says each thing once, and
  // rarely repeats a value found in the body.
  if (new Set(filled.map((c) => c.toLowerCase())).size !== filled.length) return false;
  const bodyValues = new Set(body.flatMap((r) => r.map((v) => (blank(v) ? "" : String(v).trim().toLowerCase()))).filter(Boolean));
  const reappears = filled.filter((c) => bodyValues.has(c.toLowerCase())).length;
  if (reappears > 0) return false;
  return filled.every((c) => c === tidyLabel(c) || /^[A-Z]/.test(c) || /[_]/.test(c));
}
